import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { client } from 'cccisd-apollo';
import _get from 'lodash/get';

import getAllPlansListQuery from '../../pages/SmartWrapAdminister/queries/getAllPlansList.graphql';
import getAdministeredPlansQuery from '../../pages/SmartWrapAdminister/queries/getAdministeredPlans.graphql';
import getAdministeredPlansSupervisorQuery from '../../pages/SmartWrapAdminister/queries/getAdministeredPlansSupervisor.graphql';

import { isDateInPast } from '../../pages/SmartWrapAdminister/helpers';
import ORG_CUSTOM_PLAN_ASSIGNMENT_HANDLES from '../../pages/SmartWrapAdminister/ORG_CUSTOM_PLAN_ASSIGNMENT_HANDLES.json';

const USER = window.cccisd && window.cccisd.fortress.user.acting;

const initialState = {
    loading: true,
    plansList: {},
    youthPlans: {},
};

// Plans are queried by custom assignment handle
// Example: assignmentHandle: [
//     "onboarding_youth"
//     "onboarding_caregiver"
//     "22_washington_youth" // 22 = group_id of the careCoordinator
//     "22_washington_caregiver"]
// Each surevey within an assignment is one week of questions
// Each assignment is deployed by itself for use by caregivers or youth
// The name that appears on the list of plans in the "Add Plan" Modal is based off of the deploymentHandle
export const queryAdministerPlansList = createAsyncThunk('administer/queryPlansList', async () => {
    const groupId = (USER.group_id || '-1').toString();
    const assignmentHandles = [
        ..._get(ORG_CUSTOM_PLAN_ASSIGNMENT_HANDLES, 'onboarding.handles', []),
        ..._get(ORG_CUSTOM_PLAN_ASSIGNMENT_HANDLES, `${groupId}.handles`, []), // custom state plans
    ];

    const response = await client.query({
        query: getAllPlansListQuery,
        variables: { assignment_handles: assignmentHandles },
        fetchPolicy: 'network-only',
    });

    const plans = {};
    _get(response, 'data.flows.assignmentList', []).forEach(assignment => {
        const { assignmentHandle } = assignment;

        // list of main parent deployments for each plan (weekly)
        const deploymentHandles = assignment.deploymentList.filter(
            deployment =>
                deployment.timepoint === null && deployment.parentDeploymentId === null && deployment.deploymentHandle
        );

        if (deploymentHandles.length === 0) return;

        const planName = deploymentHandles[0].deploymentHandle.split('_')[0].trim().toUpperCase();

        if (!planName) return;

        if (!plans[planName]) {
            plans[planName] = {
                planName,
                youthDeploymentHandles: [],
                caregiverDeploymentHandles: [],
                assignments: [],
                isOrgPlan: assignmentHandle.startsWith(groupId),
            };
        }

        if (deploymentHandles[0].deploymentHandle.includes('youth')) {
            plans[planName].youthDeploymentHandles = deploymentHandles.map(val => val.deploymentHandle);
        } else if (deploymentHandles[0].deploymentHandle.includes('caregiver')) {
            plans[planName].caregiverDeploymentHandles = deploymentHandles.map(val => val.deploymentHandle);
        }

        const surveys = _get(assignment, 'surveyList', []).map(survey => {
            const info = { surveyLabel: survey.label, surveyQuestions: [] };
            _get(survey, 'flowList', []).forEach(flow => {
                _get(flow, 'entityList', []).forEach(entity => {
                    if (_get(entity, 'content.question', false)) {
                        info.surveyQuestions.push(entity.content.question);
                    }
                });
            });
            return info;
        });

        plans[planName].assignments.push({ assignmentLabel: assignment.label, surveys });
    });

    return plans;
});

// query list of all administered plans for supervisors
export const querySupervisorAdministerYouthPlans = createAsyncThunk(
    'administer/querySupervisorYouthPlans',
    async () => {
        const response = await client.query({
            query: getAdministeredPlansSupervisorQuery,
            fetchPolicy: 'network-only',
        });

        const youthPlans = {};
        _get(response, 'data.roles.youthList', []).forEach(youth => {
            const { pawnId } = youth.pawn;
            const plans = youth.selectedAssignmentPlanList;

            plans.forEach(plan => {
                const settings = JSON.parse(plan.settings);
                plan.isStopped = _get(settings, 'stopPlan', false);
                plan.hasNotOpenedYet = !isDateInPast(settings.startDate);
                plan.isClosed =
                    plan.sessionList.filter(s => s.deployment.isOpen || !isDateInPast(s.opensDate)).length === 0;
                plan.wrapIds = [
                    { pawnId, wrapId: youth.fields.wrapId },
                    ...youth.parentRoles.caregiverList.map(cg => ({
                        pawnId: cg.pawn.pawnId,
                        wrapId: cg.fields.caregiverName,
                    })),
                ];
            });

            youthPlans[pawnId] = plans;
        });

        return youthPlans;
    }
);

// query list of all administered plans
export const queryAdministerYouthPlans = createAsyncThunk('administer/queryYouthPlans', async () => {
    const response = await client.query({
        query: getAdministeredPlansQuery,
        fetchPolicy: 'network-only',
        variables: { careCoordinatorPawnId: USER.id },
    });

    const youthPlans = {};
    _get(response, 'data.roles.youthList', []).forEach(youth => {
        const { pawnId } = youth.pawn;
        const plans = youth.selectedAssignmentPlanList;

        plans.forEach(plan => {
            const settings = JSON.parse(plan.settings);
            plan.isStopped = _get(settings, 'stopPlan', false);
            plan.hasNotOpenedYet = !isDateInPast(settings.startDate);
            plan.isClosed =
                plan.sessionList.filter(s => s.deployment.isOpen || !isDateInPast(s.opensDate)).length === 0;
            plan.wrapIds = [
                { pawnId, wrapId: youth.fields.wrapId },
                ...youth.parentRoles.caregiverList.map(cg => ({
                    pawnId: cg.pawn.pawnId,
                    wrapId: cg.fields.caregiverName,
                })),
            ];
        });

        youthPlans[pawnId] = plans;
    });

    return youthPlans;
});

const administerSlice = createSlice({
    name: 'administer',
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(queryAdministerPlansList.pending, state => {
                state.loading = true;
            })
            .addCase(queryAdministerPlansList.fulfilled, (state, action) => {
                state.plansList = action.payload;
                state.loading = false;
            })
            .addCase(queryAdministerPlansList.rejected, state => {
                state.loading = false;
            })
            .addCase(queryAdministerYouthPlans.pending, state => {
                state.loading = true;
            })
            .addCase(queryAdministerYouthPlans.fulfilled, (state, action) => {
                state.youthPlans = action.payload;
                state.loading = false;
            })
            .addCase(queryAdministerYouthPlans.rejected, state => {
                state.loading = false;
            })
            .addCase(querySupervisorAdministerYouthPlans.pending, state => {
                state.loading = true;
            })
            .addCase(querySupervisorAdministerYouthPlans.fulfilled, (state, action) => {
                state.youthPlans = action.payload;
                state.loading = false;
            })
            .addCase(querySupervisorAdministerYouthPlans.rejected, state => {
                state.loading = false;
            });
    },
});

export default administerSlice.reducer;
